import Card from '@/components/common/Card'
import Badge from '@/components/common/Badge'

/**
 * ThisWeekCard Component
 * 
 * Displays a prominent "This Week" overview card at the top of the portal
 * showing upcoming events, schedule changes, and key announcements.
 */
export default function ThisWeekCard({ 
  events = [], 
  wikiItems = [], 
  dispatches = [],
  teamName 
}) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const endOfWeek = new Date(today)
  endOfWeek.setDate(today.getDate() + 7)

  // Filter events to this week
  const thisWeekEvents = events
    .filter(event => {
      const eventDate = new Date(event.start_time || event.date)
      return eventDate >= today && eventDate <= endOfWeek
    })
    .sort((a, b) => {
      const dateA = new Date(a.start_time || a.date)
      const dateB = new Date(b.start_time || b.date)
      return dateA - dateB
    })

  // Get the next upcoming event
  const nextEvent = thisWeekEvents[0]

  // Find pinned wiki items (portal RPC returns is_pinned, not pinned)
  const pinnedWikiItems = wikiItems.filter(item => item.is_pinned)

  // Get recent schedule changes (wiki items with certain keywords or dispatch mentions)
  const recentChanges = wikiItems.filter(item => {
    const content = (item.content || '').toLowerCase()
    const title = (item.title || '').toLowerCase()
    return (
      content.includes('change') ||
      content.includes('moved') ||
      content.includes('cancelled') ||
      content.includes('rescheduled') ||
      title.includes('change') ||
      title.includes('update')
    )
  }).slice(0, 2)

  // Find wiki items related to upcoming events (especially tournaments)
  const getEventRelatedWiki = () => {
    const upcomingTournaments = thisWeekEvents.filter(e => 
      e.event_type === 'tournament' || 
      (e.title || '').toLowerCase().includes('tournament')
    )
    
    if (upcomingTournaments.length === 0) return []
    
    const relatedWiki = []
    
    upcomingTournaments.forEach(tournament => {
      const tournamentName = (tournament.title || '').toLowerCase()
      const tournamentLocation = (tournament.location || '').toLowerCase()
      
      const matchingWiki = wikiItems.filter(item => {
        // Don't include items we're already showing as pinned or changes
        if (item.is_pinned || pinnedWikiItems.some(p => p.id === item.id)) return false
        if (recentChanges.some(c => c.id === item.id)) return false
        
        const itemText = `${item.title || ''} ${item.content || ''} ${item.parent_key || ''}`.toLowerCase()
        // Portal RPC returns 'category', not 'section'
        const itemCategory = (item.category || item.section || '').toLowerCase()
        
        // Match by tournament name
        if (tournamentName && itemText.includes(tournamentName.split(' ')[0])) return true
        
        // Match by location if in Tournaments category
        if (itemCategory === 'tournaments' && tournamentLocation && itemText.includes(tournamentLocation.split(',')[0])) return true
        
        // Match items in Tournaments category with hotel/venue/logistics keywords
        if (itemCategory === 'tournaments' && (
          itemText.includes('hotel') ||
          itemText.includes('venue') ||
          itemText.includes('check-in') ||
          itemText.includes('checkout') ||
          itemText.includes('deadline')
        )) return true
        
        return false
      })
      
      relatedWiki.push(...matchingWiki)
    })
    
    // Remove duplicates and limit to 3
    return [...new Map(relatedWiki.map(item => [item.id, item])).values()].slice(0, 3)
  }
  
  const eventRelatedWiki = getEventRelatedWiki()

  // Get the most recent dispatch
  const latestDispatch = dispatches[0]

  // If there's nothing to show, don't render the card
  if (!nextEvent && pinnedWikiItems.length === 0 && !latestDispatch) {
    return null
  }

  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr)
    const isToday = date.toDateString() === today.toDateString()
    const isTomorrow = date.toDateString() === new Date(today.getTime() + 24*60*60*1000).toDateString()
    
    if (isToday) return 'Today'
    if (isTomorrow) return 'Tomorrow'
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  const formatEventTime = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit' 
    })
  }

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'game': return '🏒'
      case 'practice': return '⛸️'
      case 'tournament': return '🏆'
      default: return '📅'
    }
  }

  return (
    <Card className="bg-gradient-to-r from-brand-green to-green-700 text-white mb-6 overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold text-white">
            This Week
          </h2>
          <Badge variant="secondary" className="bg-white/20 text-white border-0">
            {thisWeekEvents.length} event{thisWeekEvents.length !== 1 ? 's' : ''}
          </Badge>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column: Next Event */}
          <div>
            <h3 className="text-sm text-white/80 font-medium mb-2">Next Up</h3>
            {nextEvent ? (
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{getEventTypeIcon(nextEvent.event_type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white truncate">
                      {nextEvent.title || nextEvent.event_type || 'Event'}
                    </div>
                    <div className="text-sm text-white/80 mt-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {formatEventDate(nextEvent.start_time || nextEvent.date)}
                        </span>
                        <span>at</span>
                        <span>{formatEventTime(nextEvent.start_time || nextEvent.date)}</span>
                      </div>
                      {nextEvent.location && (
                        <div className="truncate mt-1">
                          📍 {nextEvent.location}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white/10 rounded-lg p-4 text-white/70 text-center">
                No upcoming events this week
              </div>
            )}

            {/* Additional events preview */}
            {thisWeekEvents.length > 1 && (
              <div className="mt-3 space-y-2">
                {thisWeekEvents.slice(1, 3).map((event, idx) => (
                  <div 
                    key={event.id || idx} 
                    className="flex items-center gap-2 text-sm text-white/80"
                  >
                    <span>{getEventTypeIcon(event.event_type)}</span>
                    <span className="font-medium">
                      {formatEventDate(event.start_time || event.date)}
                    </span>
                    <span className="truncate flex-1">
                      {event.title || event.event_type}
                    </span>
                  </div>
                ))}
                {thisWeekEvents.length > 3 && (
                  <div className="text-sm text-white/60">
                    +{thisWeekEvents.length - 3} more events
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column: Quick Info */}
          <div>
            <h3 className="text-sm text-white/80 font-medium mb-2">Quick Info</h3>
            <div className="space-y-3">
              {/* Pinned items */}
              {pinnedWikiItems.slice(0, 2).map((item, idx) => (
                <div 
                  key={item.id || idx} 
                  className="bg-white/10 rounded-lg p-3"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-white">
                    <span>📌</span>
                    <span className="truncate">{item.title}</span>
                  </div>
                  {item.content && (
                    <div className="text-xs text-white/70 mt-1 line-clamp-2">
                      {item.content.replace(/<[^>]*>/g, '').substring(0, 100)}
                    </div>
                  )}
                </div>
              ))}

              {/* Recent changes indicator */}
              {recentChanges.length > 0 && (
                <div className="bg-amber-500/30 rounded-lg p-3 border border-amber-400/30">
                  <div className="flex items-center gap-2 text-sm font-medium text-amber-100">
                    <span>⚠️</span>
                    <span>Recent Schedule Changes</span>
                  </div>
                  <div className="text-xs text-amber-200/80 mt-1">
                    {recentChanges.length} update{recentChanges.length !== 1 ? 's' : ''} - check wiki for details
                  </div>
                </div>
              )}

              {/* Latest dispatch preview */}
              {latestDispatch && (
                <div className="bg-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-white">
                    <span>📣</span>
                    <span className="truncate">{latestDispatch.title}</span>
                  </div>
                  <div className="text-xs text-white/70 mt-1">
                    {new Date(latestDispatch.sent_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              )}

              {/* Empty state if nothing to show */}
              {pinnedWikiItems.length === 0 && recentChanges.length === 0 && !latestDispatch && (
                <div className="bg-white/10 rounded-lg p-4 text-white/70 text-center text-sm">
                  No announcements this week
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Event-Related Wiki Section */}
        {eventRelatedWiki.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <h3 className="text-sm text-white/80 font-medium mb-2">
              📋 Important Info for This Week
            </h3>
            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
              {eventRelatedWiki.map((item, idx) => (
                <div 
                  key={item.id || idx} 
                  className="bg-white/10 rounded-lg p-3"
                >
                  <p className="text-sm font-medium text-white truncate">
                    {item.title}
                  </p>
                  <p className="text-xs text-white/70 line-clamp-2 mt-1">
                    {(item.content || '').replace(/<[^>]*>/g, '').substring(0, 100)}
                  </p>
                  {(item.category || item.section) && (
                    <p className="text-xs text-white/50 mt-1">
                      {item.category || item.section}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}
